/**
 * URL validation helpers for safely rendering links and image sources sourced
 * from inputs an app does not generate itself (form fields, query parameters,
 * router/Morpho API responses, RPC, token metadata, ENS).
 *
 * The browser interprets `javascript:`, `data:`, `vbscript:`, and similar
 * schemes as code-execution vectors when placed in `href`/`src` attributes.
 * Pass any externally sourced URL through `safeExternalHref` (or
 * `safeExternalImageSrc` for images) before binding it to the DOM.
 */

const DEFAULT_HREF_PROTOCOLS: ReadonlyArray<string> = ['http:', 'https:', 'mailto:']
const DEFAULT_IMAGE_PROTOCOLS: ReadonlyArray<string> = ['http:', 'https:']

interface SafeUrlOptions {
  /**
   * Allowed URL protocols (with trailing colon, e.g. `https:`). Anything
   * outside this allowlist returns `undefined`.
   */
  allowedProtocols?: ReadonlyArray<string>
}

/**
 * Returns the URL string when its protocol is in `allowedProtocols`, otherwise
 * `undefined`. Relative URLs (no scheme) and protocol-relative URLs are
 * rejected because their effective protocol depends on the document and can
 * be coerced into something unsafe.
 */
export function safeUrl(
  raw: string | null | undefined,
  options: SafeUrlOptions = {}
): string | undefined {
  if (typeof raw !== 'string') return undefined
  const trimmed = raw.trim()
  if (!trimmed) return undefined

  // Reject protocol-relative URLs early — `URL` would resolve them against a
  // base, but consumers rendering them as `href`/`src` would inherit whatever
  // protocol the page is served over.
  if (trimmed.startsWith('//')) return undefined

  let parsed: URL
  try {
    parsed = new URL(trimmed)
  } catch {
    return undefined
  }

  const allowed = options.allowedProtocols ?? DEFAULT_HREF_PROTOCOLS
  if (!allowed.includes(parsed.protocol)) return undefined

  return parsed.toString()
}

const INTERNAL_SENTINEL_ORIGIN = 'https://internal.invalid'

/**
 * Resolves `href` against a sentinel origin and returns the URL only when it
 * keeps that origin. `//x` is a protocol-relative URL, WHATWG reads `\` as `/`
 * and strips `\t`/`\n`/`\r`, so `/\x` and `/\t/x` collapse to `//x` and resolve
 * off-origin. Dot segments can also normalise to a `//x` pathname (`/a/..//x`)
 * that keeps the origin but turns protocol-relative once serialised.
 */
function resolveInternalPath(href: string): URL | undefined {
  if (!href.startsWith('/')) return undefined
  try {
    const url = new URL(href, INTERNAL_SENTINEL_ORIGIN)
    return url.origin === INTERNAL_SENTINEL_ORIGIN && !url.pathname.startsWith('//')
      ? url
      : undefined
  } catch {
    return undefined
  }
}

/** True only for genuine same-origin paths; see {@link safeInternalPath}. */
export function isInternalPath(href: string): boolean {
  return resolveInternalPath(href) !== undefined
}

/**
 * Returns `href` as a normalised same-origin path (pathname, search and hash),
 * so characters the URL parser strips cannot come back, or `undefined` when it
 * would resolve off-origin.
 */
export function safeInternalPath(href: string): string | undefined {
  const url = resolveInternalPath(href)
  return url ? `${url.pathname}${url.search}${url.hash}` : undefined
}

/**
 * Validates a URL intended for an anchor `href`. Returns the URL on success
 * and `undefined` when the URL is malformed or uses a disallowed scheme such
 * as `javascript:` or `data:`.
 */
export function safeExternalHref(raw: string | null | undefined): string | undefined {
  return safeUrl(raw, { allowedProtocols: DEFAULT_HREF_PROTOCOLS })
}

/**
 * Validates a URL intended for an image `src`. Stricter than
 * `safeExternalHref` because image renderers will happily decode `data:` URIs.
 */
export function safeExternalImageSrc(raw: string | null | undefined): string | undefined {
  return safeUrl(raw, { allowedProtocols: DEFAULT_IMAGE_PROTOCOLS })
}
